
import { TeamListResponseDto } from "@/lib/api/interfaces/teams.interface";
import { useTeamsFindQuery, UseTeamsFindParams, useTeamsDelete, useTeamsBulkDelete } from "@/lib/api/requests/teams.requests";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import { getAbbreviatedTeamName } from "@/lib/utils/team-utils";
import {
  DataView,
  DataViewGridCard,
  DataViewLayout,
  DataViewListCard,
  DataViewLoadingState,
  GenericItem,
  DataViewContent,
  DataViewEmptyState,
  DataViewLoadMore,
  GenericItemActions,
  EntityPreviewFrame
} from "@/util-components/data-view";
import { DataViewToolbar } from "@/util-components/data-view/data-view-toolbar";
import { PageHeader, PageLayout, PageTitle, PrimaryActionButton } from "@/util-components/page-layout";
import { Plus, UsersRound } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import { usePageState } from "@/lib/hooks/use-page-state";
import CreateTeamDialog from "./create-dialog";
import TeamDetailsDialog from "./details-dialog";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";


const DEFAULT_PAGE_SIZE = 20;

const TeamsWrapper = () => {
  return (
    <PageLayout basePath="/teams" defaultTab="teams">
      <Teams />
    </PageLayout>
  );
};

const Teams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, setViewMode } = usePageState('teams-page', { viewMode: 'grid' });
  const viewMode = state.viewMode || 'grid';
  
  const [searchTerm, setSearchTerm] = useState(searchParams.get('textSearch') || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [allTeams, setAllTeams] = useState<TeamListResponseDto[]>([]);
  const [lastFilterKey, setLastFilterKey] = useState<string>('');

  // Dialog states
  const [detailsPopupOpen, setDetailsPopupOpen] = useState<{
    open: boolean;
    user: TeamListResponseDto | null;
    mode: "details" | "edit";
  }>({
    open: false,
    user: null,
    mode: "details",
  });

  const [createTeamDialogOpen, setCreateTeamDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; team: TeamListResponseDto | null; isBulk?: boolean }>({
    open: false,
    team: null,
    isBulk: false
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { mutate: deleteTeam, isPending: isDeleting } = useTeamsDelete();
  const { mutate: bulkDeleteTeams, isPending: isBulkDeleting } = useTeamsBulkDelete();

  // Filter params
  const currentFilterKey = useMemo(() => {
    return `${searchTerm.trim()}`;
  }, [searchTerm]);

  const params: UseTeamsFindParams = useMemo(() => ({
    textSearch: searchTerm.trim() || undefined,
    page: currentPage,
    limit: DEFAULT_PAGE_SIZE,
  }), [searchTerm, currentPage]);

  // Query
  const { data, isLoading, refetch, isError } = useTeamsFindQuery(params, {
    skipGlobalErrorHandler: true
  });

  // Show toast on error
  useEffect(() => {
    if (isError) {
      toast.error("Fehler beim Laden der Mannschaften");
    }
  }, [isError]);
  
  // Reset on filter change
  useEffect(() => {
    if (currentFilterKey !== lastFilterKey) {
      setCurrentPage(1);
      setAllTeams([]);
      setLastFilterKey(currentFilterKey);
    }
  }, [currentFilterKey, lastFilterKey]);

  // Accumulate data
  useEffect(() => {
    if (data?.docs) {
      if (currentPage === 1) {
        setAllTeams(data.docs);
      } else {
        setAllTeams(prev => {
          // Prevent duplicates if React Strict Mode causes double invoke
          const newIds = new Set(data.docs.map(d => d._id));
          return [...prev.filter(p => !newIds.has(p._id)), ...data.docs];
        });
      }
    }
  }, [data, currentPage]);

  // Sync URL params
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (searchTerm) {
      newParams.set('textSearch', searchTerm);
    } else {
      newParams.delete('textSearch');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchTerm, setSearchParams, searchParams]);

  const hasMore = data?.hasNextPage || false;
  const isInitialLoading = isLoading && currentPage === 1;
  const isLoadingMore = isLoading && currentPage > 1;

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearchTerm(text);
  };

  const handleDelete = async () => {
    if (deleteConfirm.isBulk && selectedIds.length > 0) {
      await bulkDeleteTeams({ ids: selectedIds }, {
        onSuccess: async () => {
            toast.success(`${selectedIds.length} Mannschaften erfolgreich gelöscht`);
            await refetch();
            setCurrentPage(1); 
            setAllTeams(prev => prev.filter(t => !selectedIds.includes(t._id)));
            setSelectedIds([]);
            setDeleteConfirm({ open: false, team: null, isBulk: false });
        },
        onError: (error) => {
            toast.error("Mannschaften konnten nicht gelöscht werden", { error });
        }
      });
      return;
    }

    if (!deleteConfirm.team) return;

    await deleteTeam({ id: deleteConfirm.team._id }, {
      onSuccess: async () => {
          toast.success("Mannschaft erfolgreich gelöscht");
          await refetch();
          setCurrentPage(1); 
          setAllTeams(prev => prev.filter(t => t._id !== deleteConfirm.team!._id));
          setDeleteConfirm({ open: false, team: null });
      },
      onError: (error) => {
          toast.error("Mannschaft konnte nicht gelöscht werden", { error });
      }
    });
  };

  const handleSelectionDelete = () => {
    if (selectedIds.length > 0) {
      setDeleteConfirm({ open: true, team: null, isBulk: true });
    }
  };

  const handleCreateSuccess = async () => {
    await refetch();
    setCurrentPage(1);
  };

  const handleDetailsClose = async (shouldRefetch?: boolean) => {
    setDetailsPopupOpen({ open: false, user: null, mode: "details" });
    if (shouldRefetch) {
      await refetch();
      setCurrentPage(1);
    }
  };

  // Convert to GenericItems
  const convertToGenericItem = useCallback((team: TeamListResponseDto): GenericItem => {
    const abbreviation = team.title ? getAbbreviatedTeamName(team.title, {preferLeadingNumber: false}) : "";

    return {
      id: team._id,
      title: team.title,
      description: team.displayName || "Mannschaft",
      previewContent: (
        <EntityPreviewFrame backgroundUrl={team.logoUrl}>
          {team.logoUrl ? (
            <img 
              src={team.logoUrl} 
              alt={team.title} 
              className="max-h-full max-w-full object-contain" 
            />
          ) : (
            <span className="text-2xl font-bold tracking-tight text-blue-600 uppercase">
              {abbreviation}
            </span>
          )}
        </EntityPreviewFrame>
      ),
      previewContentList: (
        <div className="w-full h-full flex items-center justify-center bg-blue-50">
            {team.logoUrl ? (
                <img src={team.logoUrl} alt="" className="h-6 w-6 object-contain" />
            ) : (
                <span className="text-[10px] font-bold text-blue-600 uppercase">
                    {abbreviation}
                </span>
            )}
        </div>
      )
    };
  }, []);

  const genericItems = useMemo(() => {
    return allTeams.map(convertToGenericItem);
  }, [allTeams, convertToGenericItem]);

  const genericActions: GenericItemActions = {
    onItemClick: (id) => {
      const team = allTeams.find(t => t._id === id);
      if (team) setDetailsPopupOpen({ open: true, user: team, mode: "details" });
    },
    onEdit: (id) => {
      const team = allTeams.find(t => t._id === id);
      if (team) setDetailsPopupOpen({ open: true, user: team, mode: "edit" });
    },
    onDelete: (id) => {
      const team = allTeams.find(t => t._id === id);
      if (team) setDeleteConfirm({ open: true, team });
    }
  };

  return (
    <div className="h-full flex flex-col">
       <PageHeader>
           <PageTitle 
             title="Mannschaften" 
             icon={UsersRound} 
             iconColor="text-blue-600"
           />
       </PageHeader>

      <DataView 
        viewMode={viewMode} 
        onViewModeChange={setViewMode}
        className="flex-1 bg-transparent"
        isLoading={isInitialLoading}
        isEmpty={allTeams.length === 0}
        selectedIds={selectedIds}
        onMultiSelect={setSelectedIds}
        onSelectionDelete={handleSelectionDelete}
      >
        <div className="flex flex-col h-full">
          <DataViewToolbar
            searchQuery={searchTerm}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Mannschaften suchen..."
            infoText={`${data?.count || 0} Mannschaften`}
            leading={
              <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                <PrimaryActionButton 
                  onClick={() => setCreateTeamDialogOpen(true)}
                  label="Mannschaft hinzufügen"
                  icon={Plus}
                />
              </PermissionFeatureWrapper>
            }
          />

          <DataViewLayout>
            <DataViewContent
              items={genericItems}
              renderGridItem={(item) => (
                <DataViewGridCard
                  item={item}
                  actions={genericActions}
                />
              )}
              renderListItem={(item) => (
                <DataViewListCard
                  item={item}
                  actions={genericActions}
                />
              )}
              loadingIndicator={<DataViewLoadingState />}
              emptyState={
                <DataViewEmptyState
                  icon={<UsersRound className="h-8 w-8 text-gray-400" />}
                  title={searchTerm ? 'Keine Mannschaften gefunden' : 'Keine Mannschaften'}
                  description={searchTerm 
                    ? 'Versuchen Sie andere Suchbegriffe.'
                    : 'Erstellen Sie Ihre erste Mannschaft.'
                  }
                  action={
                    !searchTerm ? (
                      <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                        <PrimaryActionButton 
                          onClick={() => setCreateTeamDialogOpen(true)}
                          label="Mannschaft erstellen"
                          icon={Plus}
                        />
                      </PermissionFeatureWrapper>
                    ) : undefined
                  }
                />
              }
            />
            
            <DataViewLoadMore
               onLoadMore={handleLoadMore}
               isLoadingMore={isLoadingMore}
               hasMore={hasMore}
            />
          </DataViewLayout>
        </div>

        {/* Dialogs */}
        {detailsPopupOpen.open && detailsPopupOpen.user && (
          <TeamDetailsDialog
            open={detailsPopupOpen.open}
            teamId={detailsPopupOpen.user._id}
            mode={detailsPopupOpen.mode}
            onClose={handleDetailsClose}
          />
        )}

        {createTeamDialogOpen && (
          <CreateTeamDialog
            open={createTeamDialogOpen}
            onClose={(refetchData) => {
              setCreateTeamDialogOpen(false);
              if (refetchData) {
                handleCreateSuccess();
              }
            }}
          />
        )}
        
        <StandardDeleteDialog 
          open={deleteConfirm.open} 
          onOpenChange={(val) => setDeleteConfirm(prev => ({ ...prev, open: val }))}
          onDelete={handleDelete}
          title={deleteConfirm.isBulk ? `${selectedIds.length} Mannschaften löschen` : "Mannschaft löschen"}
          description={deleteConfirm.isBulk ? "Möchten Sie diese Mannschaften wirklich unwiderruflich löschen?" : "Diese Aktion kann nicht rückgängig gemacht werden"}
          itemTitle={deleteConfirm.team ? deleteConfirm.team.title : undefined}
          itemTypeLabel={deleteConfirm.team ? "ID" : undefined}
          itemTypeValue={deleteConfirm.team?._id}
          itemsList={deleteConfirm.isBulk ? selectedIds.map(id => {
            const t = allTeams.find(t => t._id === id);
            return {
              title: t ? t.title : id,
              subtitle: t?.displayName || undefined
            };
          }) : undefined}
          isLoading={isDeleting || isBulkDeleting}
        />
      </DataView>
    </div>
  );
};

export default TeamsWrapper;
